import React from 'react';
import { formatCurrency } from '../../utils/formatCurrency';

interface MonteCarloCardProps {
  data?: {
    iterations: number;
    results: {
      base_claim: number;
      results: {
        mean: number;
        median: number;
        std_dev: number;
        ci_95_low: number;
        ci_95_high: number;
        min: number;
        max: number;
      };
    };
    run_at: string;
  };
  monteCarloLoading: boolean;
  onRunAnalysis: () => void;
}

export const MonteCarloCard: React.FC<MonteCarloCardProps> = ({
  data,
  monteCarloLoading,
  onRunAnalysis,
}) => {
  const mc = data;
  const mcResults = mc?.results?.results;

  return (
    <div className="bg-white rounded-xl p-6 border border-blue-200 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold flex items-center gap-2">
          <span className="text-2xl">🎲</span>
          Monte Carlo Simulation
        </h2>
        {mc && (
          <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">✓ Complete</span>
        )}
      </div>
      
      {/* Explanation */}
      <div className="bg-blue-50 rounded-lg p-4 mb-4">
        <p className="text-sm text-gray-700">
          <strong>What is Monte Carlo Simulation?</strong> This runs 1,000 random variations of your claim calculation to determine the statistical probability of different outcomes. The 95% confidence interval means there's a 95% chance the true claim value falls within this range — providing strong statistical backing for your claim.
        </p>
      </div>
      
      {mc ? (
        <div className="space-y-4">
          {/* Results Display */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Iterations</p>
              <p className="text-lg font-bold text-blue-700">{mc.iterations?.toLocaleString()}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Mean</p>
              <p className="text-lg font-bold text-gray-900">{formatCurrency(mcResults?.mean || 0)}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Median</p>
              <p className="text-lg font-bold text-gray-900">{formatCurrency(mcResults?.median || 0)}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Std Dev</p>
              <p className="text-lg font-bold text-blue-700">{formatCurrency(mcResults?.std_dev || 0)}</p>
            </div>
          </div>
          
          {/* 95% Confidence Interval */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4">
            <p className="text-sm text-gray-600 mb-3 font-medium">95% Confidence Interval</p>
            <div className="flex items-center gap-3">
              <div className="text-center">
                <p className="text-xs text-gray-500">Low</p>
                <p className="text-sm font-bold text-blue-700">{formatCurrency(mcResults?.ci_95_low || 0)}</p>
              </div>
              <div className="flex-1 relative">
                <div className="h-4 bg-gradient-to-r from-blue-200 via-blue-400 to-blue-200 rounded-full"></div>
                <div className="absolute left-1/2 top-0 w-1 h-4 bg-yellow-500 rounded transform -translate-x-1/2" title="Median"></div>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-500">High</p>
                <p className="text-sm font-bold text-blue-700">{formatCurrency(mcResults?.ci_95_high || 0)}</p>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2 text-center">
              There's a 95% probability your true claim value falls within this range
            </p>
          </div>
          
          <button
            onClick={onRunAnalysis}
            disabled={monteCarloLoading}
            className="w-full py-2 px-4 bg-blue-100 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-200 transition disabled:opacity-50"
          >
            {monteCarloLoading ? '⏳ Re-running...' : '🔄 Re-run Simulation'}
          </button>
          
          <p className="text-xs text-gray-400">Last run: {new Date(mc.run_at).toLocaleString()}</p>
        </div>
      ) : (
        <div className="space-y-4">
          <button
            onClick={onRunAnalysis}
            disabled={monteCarloLoading}
            className="w-full py-3 px-4 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 transition disabled:opacity-50"
          >
            {monteCarloLoading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                </svg>
                Running 1,000 Simulations...
              </span>
            ) : 'Run Monte Carlo Simulation (1,000 iterations)'}
          </button>
        </div>
      )}
    </div>
  );
};

export default MonteCarloCard;
